import { FC, useState } from 'react';
import Grid from '@mui/material/Grid';
import ponchoImg from '../../media/poncho.png';
import jaroImg from '../../media/jaro.png';
import basiaImg from '../../media/basia.png';
import zusiaImg from '../../media/zusia.png';
import { Typography, Popper, Box, ClickAwayListener } from '@mui/material';
import useTranslations from '../global/translation/hooks/translation';
import { JobModal } from '../global/JobModal';
import { useTheme } from '@mui/material/styles';
import { useThemeContext } from '../global/ThemeContext';
import { keyframes } from '@emotion/react';
import useImagePreload from '../../hooks/useImagePreload';

const Team: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const theme = useTheme();
  const percentAmount = '8rem';
  const { darkMode } = useThemeContext();
  const [selectedMember, setSelectedMember] = useState<{ name: string; description: string } | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>(null);

  const pulse = keyframes`
    0% { transform: translateY(-6px) scale(1.03); }
    50% { transform: translateY(-10px) scale(1.06); }
    100% { transform: translateY(-6px) scale(1.03); }
  `;

  // Warm the cache for team images on first mount
  useImagePreload([ponchoImg, basiaImg, jaroImg, zusiaImg]);

  const handleClose = () => {
    setSelectedMember(null);
    setAnchorEl(null);
  };

  const teamMembers = [
    {
      name: currentLanguage === 'pl' ? 'Poncho' : 'Poncho',
      description: translations.label?.teamPonchoDesc?.[currentLanguage] || (currentLanguage === 'pl' ? 'Koń treningowy' : 'Training Horse'),
      img: ponchoImg,
    },
    {
      name: currentLanguage === 'pl' ? 'Barbara' : 'Barbara',
      description: translations.label?.teamBasiaDesc?.[currentLanguage] || (currentLanguage === 'pl' ? 'Główny szkoleniowiec' : 'Head Trainer'),
      img: basiaImg,
    },
    {
      name: currentLanguage === 'pl' ? 'Jarosław' : 'Jarosław',
      description: translations.label?.teamJaroDesc?.[currentLanguage] || (currentLanguage === 'pl' ? 'Asystent trenera' : 'Trainer Assistant'),
      img: jaroImg,
    },
    {
      name: currentLanguage === 'pl' ? 'Zuzanna' : 'Susanne',
      description: translations.label?.teamZosiaDesc?.[currentLanguage] || (currentLanguage === 'pl' ? 'Opiekun koni' : 'Horse Care Specialist'),
      img: zusiaImg,
    },
  ];
  return (
    <Grid
      my={6}
      py={2}
      container
      justifyContent="center"
      alignItems="center"
      sx={{
        bgcolor: darkMode ? '#181818' : '#fff',
        px: { xs: 2, sm: 3 },
      }}
    >
      <Grid item xs={12} m={2}>
        <Typography variant="h4" fontWeight={300} sx={{ color: darkMode ? '#fff' : '#121212' }} textAlign="center">
          {translations.menuItems.ourTeam[currentLanguage]}
        </Typography>
      </Grid>
      <Grid container justifyContent="center" alignItems="center" onClick={handleClose}>
        {teamMembers.map((member, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Box
              sx={{
                position: 'relative',
                paddingTop: percentAmount,
                m: 2,
                cursor: 'pointer',
                transition: 'transform 200ms ease',
                transform: selectedMember?.name === member.name ? 'none' : 'translateY(0) scale(1)',
                animation: selectedMember?.name === member.name ? `${pulse} 2s ease-in-out infinite` : 'none',
                '&:hover': {
                  transform: selectedMember?.name === member.name ? 'none' : 'translateY(-8px) scale(1.02)',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMember(member);
                setAnchorEl(e.currentTarget);
              }}
            >
              <Box
                component="img"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'scale-down',
                  transition: 'transform 220ms ease',
                  transform: selectedMember?.name === member.name ? 'scale(1.02)' : 'scale(1)',
                }}
                src={member.img}
                alt={member.name}
                loading={index < 2 ? 'eager' : 'lazy'}
                decoding="async"
                width={256}
                height={256}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
      <Grid item xs={12}>
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs>
            <JobModal percentAmount={percentAmount} />
          </Grid>
        </Grid>
      </Grid>

      <Popper
        open={!!selectedMember}
        anchorEl={anchorEl}
        placement="bottom"
        modifiers={[
          {
            name: 'flip',
            enabled: true,
            options: {
              altBoundary: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: 'document',
              padding: 8,
            },
          },
          {
            name: 'arrow',
            enabled: true,
            options: {
              element: arrowRef,
            },
          },
          {
            name: 'offset',
            enabled: true,
            options: {
              offset: [0, 8],
            },
          },
        ]}
        sx={{
          zIndex: 1300,
          '&[data-popper-placement*="bottom"] .MuiPopper-arrow': {
            top: 0,
            left: 0,
            marginTop: '-0.71em',
            width: '1em',
            height: '0.71em',
            '&::before': {
              content: '""',
              margin: 'auto',
              display: 'block',
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '0 0.71em 0.71em 0.71em',
              borderColor: `transparent transparent ${darkMode ? '#232323' : theme.palette.background.paper} transparent`,
            },
          },
        }}
      >
        <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseDown">
          <Box
            sx={{
              backgroundColor: darkMode ? '#232323' : theme.palette.background.paper,
              boxShadow: theme.shadows[3],
              borderRadius: 1.5,
              p: 2,
              position: 'relative',
            }}
          >
            <Box ref={setArrowRef} className="MuiPopper-arrow" />
            <Typography variant="h6" sx={{ color: darkMode ? '#fff' : '#000', fontWeight: 600, mb: 1 }}>
              {selectedMember?.name}
            </Typography>
            <Typography variant="body2" sx={{ color: darkMode ? '#ccc' : '#333' }}>
              {selectedMember?.description}
            </Typography>
          </Box>
        </ClickAwayListener>
      </Popper>
    </Grid>
  );
};

export default Team;
